import { type FormEvent, useEffect, useRef, useState } from 'react'
import { ApiError } from '@services/apiClient'
import { LOGIN_ROUTE } from '@constants/routes'
import { resendVerification as resendVerificationApi } from '../api/authApi'
import { loginWithMasterPassword, type TwoFactorPendingHandle } from '../services/loginFlow'
import { sessionStore } from '../services/sessionStore'

/**
 * ログインフォーム用 state machine + onSubmit ハンドラ。
 *
 * - UI とロジックの分離: state 遷移 / API 呼出 / エラー文言 key 決定 をこのフックに集約し、
 *   LoginPage.tsx は描画 + t(messageKey) のみに専念する。
 * - エラー文言は i18n key を返し、描画側で `t()` を当てる (UI 文言の直書き禁止)。
 * - submitting 中は重複送信を防止する (submittingRef)
 * - 認証済から「ロック」したときは sessionStore を完全クリア + #login へ誘導
 *
 * Phase 4 #44 Phase B (2FA login):
 *   - login 応答が `twoFactorRequired:true` の場合は `two_factor_required` state に
 *     遷移し、pending handle を ref で保持する。
 *   - `submitTwoFactorCode(code)` が pending.submit を呼び、成功で `authenticated` へ。
 *   - `cancelTwoFactor()` または unmount で pending.dispose() を必ず呼び `_smkEnc` を
 *     zeroize する (CLAUDE.md §2.3 / KEY_HIERARCHY.md)。
 */

export type LoginFormState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'two_factor_required' }
  | { kind: 'two_factor_submitting' }
  | { kind: 'authenticated' }
  | { kind: 'error'; messageKey: string }
  | { kind: 'two_factor_error'; messageKey: string }
  /** T-H2: メール未認証で 403。再送導線を出すため email を保持する。 */
  | { kind: 'email_unverified'; email: string }

/** 確認メール再送ボタンの状態。 */
export type ResendStatus = 'idle' | 'sending' | 'sent'

/**
 * 確認メール再送のクールダウン秒数。連打によるメール送信リソースの濫用を防ぐ。
 * バックエンドの RESEND_COOLDOWN_MS (2 分) と揃える。
 */
const RESEND_COOLDOWN_SEC = 120

const ERROR_MESSAGE_KEYS = {
  unauthorized: 'auth.error.unauthorized',
  emailNotVerified: 'auth.error.email_not_verified',
  accountDeleted: 'auth.error.account_deleted',
  generic: 'auth.error.generic',
  twoFactorInvalid: 'auth.two_factor.error.invalid_code',
  twoFactorExpired: 'auth.two_factor.error.expired',
  twoFactorTooMany: 'auth.two_factor.error.too_many_requests',
  twoFactorGeneric: 'auth.two_factor.error.generic',
} as const

const messageKeyForLogin = (err: unknown): string => {
  if (err instanceof ApiError) {
    // 410 Gone: 退会済アカウント。SRP は通っているので本人確認済 (列挙されないことが保証)。
    if (err.status === 410) return ERROR_MESSAGE_KEYS.accountDeleted
    // T-H2: メール未認証は 403。SRP は通っているのでパスワードは正しい。
    if (err.status === 403) return ERROR_MESSAGE_KEYS.emailNotVerified
    if (err.status === 401) return ERROR_MESSAGE_KEYS.unauthorized
  }

  return ERROR_MESSAGE_KEYS.generic
}

const messageKeyForTwoFactor = (err: unknown): string => {
  if (err instanceof ApiError) {
    if (err.status === 401) return ERROR_MESSAGE_KEYS.twoFactorInvalid
    if (err.status === 400) return ERROR_MESSAGE_KEYS.twoFactorExpired
    if (err.status === 429) return ERROR_MESSAGE_KEYS.twoFactorTooMany
  }

  return ERROR_MESSAGE_KEYS.twoFactorGeneric
}

export interface UseLoginForm {
  state: LoginFormState
  isSubmitting: boolean
  onSubmit: (ev: FormEvent<HTMLFormElement>) => Promise<void>

  /** 2FA pending 中に 6 桁 TOTP / recovery code を送信する */
  submitTwoFactorCode: (code: string) => Promise<void>

  /** 2FA pending 中に email recovery 用 6 桁 一時コードを送信リクエスト */
  requestTwoFactorEmailRecovery: () => Promise<void>

  /** 2FA pending 中に メール受信した 6 桁 code で login completion */
  submitTwoFactorEmailRecovery: (code: string) => Promise<void>

  /** 2FA pending をキャンセル (`_smkEnc` を zeroize して idle に戻る) */
  cancelTwoFactor: () => void

  /** 認証済みからロックして再ログイン待ちに戻す。 */
  lock: () => void

  /** T-H2: メール未認証 (`email_unverified`) 時の確認メール再送ステータス。 */
  resendStatus: ResendStatus

  /** T-H2: 確認メールを再送する (`email_unverified` 状態でのみ有効)。 */
  resendVerification: () => Promise<void>

  /** T-H2: 再送クールダウンの残り秒数 (0 = 再送可能)。連打防止用。 */
  resendCooldownSec: number
}

export const useLoginForm = (): UseLoginForm => {
  const [state, setState] = useState<LoginFormState>(() =>
    sessionStore.isAuthenticated() ? { kind: 'authenticated' } : { kind: 'idle' },
  )

  const [resendStatus, setResendStatus] = useState<ResendStatus>('idle')
  /** 再送クールダウンの残り秒数。0 = 再送可能。login 再送信を跨いで保持する。 */
  const [resendCooldownSec, setResendCooldownSec] = useState(0)

  const submittingRef = useRef(false)
  const pendingRef = useRef<TwoFactorPendingHandle | null>(null)

  // unmount 時に pending を dispose (`_smkEnc` zeroize)
  useEffect(() => {
    return (): void => {
      pendingRef.current?.dispose()
      pendingRef.current = null
    }
  }, [])

  // 再送クールダウンを 1 秒ごとに減算する。
  useEffect(() => {
    if (resendCooldownSec <= 0) return undefined

    const id = window.setInterval(() => {
      setResendCooldownSec(s => Math.max(0, s - 1))
    }, 1000)

    return (): void => window.clearInterval(id)
  }, [resendCooldownSec])

  const onSubmit = async (ev: FormEvent<HTMLFormElement>): Promise<void> => {
    ev.preventDefault()
    if (submittingRef.current) return

    // ev.currentTarget は await を跨ぐと null 化されるため、ここで参照を固定する
    // (React の合成イベントの仕様に起因する典型的な落とし穴)。
    const form = ev.currentTarget
    const data = new FormData(form)
    const email = String(data.get('email') ?? '')
    const password = String(data.get('password') ?? '')
    // checkbox は checked 時のみ FormData に登場する。値の有無で判定する。
    const trustDevice = data.get('trustDevice') !== null

    submittingRef.current = true
    setState({ kind: 'submitting' })
    setResendStatus('idle')

    // 前回 pending が残っていれば dispose (再ログイン時の safety)
    pendingRef.current?.dispose()
    pendingRef.current = null

    try {
      const result = await loginWithMasterPassword(email, password, { trustDevice })

      if (result.kind === 'two_factor_required') {
        pendingRef.current = result.pending
        setState({ kind: 'two_factor_required' })
      } else {
        setState({ kind: 'authenticated' })
        form.reset()
      }
    } catch (err: unknown) {
      // T-H2: 403 = メール未認証。SRP は通っており email は正しいので、
      // 再送導線付きの専用 state に遷移する (汎用 error にはしない)。
      if (err instanceof ApiError && err.status === 403) {
        setState({ kind: 'email_unverified', email })
      } else {
        setState({ kind: 'error', messageKey: messageKeyForLogin(err) })
      }
    } finally {
      submittingRef.current = false
    }
  }

  const submitTwoFactorCode = async (code: string): Promise<void> => {
    if (submittingRef.current) return
    const pending = pendingRef.current
    if (pending === null) {
      setState({ kind: 'two_factor_error', messageKey: ERROR_MESSAGE_KEYS.twoFactorGeneric })

      return
    }

    submittingRef.current = true
    setState({ kind: 'two_factor_submitting' })
    try {
      await pending.submit(code)
      pendingRef.current = null
      setState({ kind: 'authenticated' })
    } catch (err: unknown) {
      // pending は再試行可能なので残す (failure 時は zeroize しない)
      setState({ kind: 'two_factor_error', messageKey: messageKeyForTwoFactor(err) })
    } finally {
      submittingRef.current = false
    }
  }

  const requestTwoFactorEmailRecovery = async (): Promise<void> => {
    if (submittingRef.current) return
    const pending = pendingRef.current
    if (pending === null) {
      setState({ kind: 'two_factor_error', messageKey: ERROR_MESSAGE_KEYS.twoFactorGeneric })

      return
    }

    submittingRef.current = true
    try {
      await pending.requestEmailRecovery()
      // 成功 → UI 上で 6 桁 code 入力モードへ移行 (state は変えず、UI 側で flag 管理)。
      // 失敗は warning として error state を反映する。
    } catch (err: unknown) {
      setState({ kind: 'two_factor_error', messageKey: messageKeyForTwoFactor(err) })
    } finally {
      submittingRef.current = false
    }
  }

  const submitTwoFactorEmailRecovery = async (code: string): Promise<void> => {
    if (submittingRef.current) return
    const pending = pendingRef.current
    if (pending === null) {
      setState({ kind: 'two_factor_error', messageKey: ERROR_MESSAGE_KEYS.twoFactorGeneric })

      return
    }

    submittingRef.current = true
    setState({ kind: 'two_factor_submitting' })
    try {
      await pending.submitEmailRecovery(code)
      pendingRef.current = null
      setState({ kind: 'authenticated' })
    } catch (err: unknown) {
      setState({ kind: 'two_factor_error', messageKey: messageKeyForTwoFactor(err) })
    } finally {
      submittingRef.current = false
    }
  }

  const cancelTwoFactor = (): void => {
    pendingRef.current?.dispose()
    pendingRef.current = null
    setState({ kind: 'idle' })
  }

  const lock = (): void => {
    sessionStore.clear()
    setState({ kind: 'idle' })
    window.location.hash = LOGIN_ROUTE
  }

  const resendVerification = async (): Promise<void> => {
    if (state.kind !== 'email_unverified' || resendStatus === 'sending' || resendCooldownSec > 0) {
      return
    }
    setResendStatus('sending')
    try {
      await resendVerificationApi(state.email)
      setResendStatus('sent')
      // 連打防止: 送信後はクールダウンを開始する (login 再送信を跨いで継続)。
      setResendCooldownSec(RESEND_COOLDOWN_SEC)
    } catch {
      // 通信失敗時は idle に戻し再試行可能にする (サーバは Phantom 設計で常に 200)。
      setResendStatus('idle')
    }
  }

  return {
    state,
    isSubmitting: state.kind === 'submitting' || state.kind === 'two_factor_submitting',
    onSubmit,
    submitTwoFactorCode,
    requestTwoFactorEmailRecovery,
    submitTwoFactorEmailRecovery,
    cancelTwoFactor,
    lock,
    resendStatus,
    resendVerification,
    resendCooldownSec,
  }
}
